import React from 'react'
import Modal from '../../components/Modal'
import Button from '../../components/Button'
import profileIcon from '../../assets/icons/profile-icon.svg'
import { AppFeedback } from '../../interfaces/feedback'

interface FeedbackDetailsModalProps {
  open: boolean
  onClose: () => void
  feedback: AppFeedback | null
  onReply?: (feedback: AppFeedback) => void
}

const FeedbackDetailsModal: React.FC<FeedbackDetailsModalProps> = ({ 
  open, 
  onClose, 
  feedback,
  onReply 
}) => {
  const formatDateTime = (date: string) => {
    const d = new Date(date)
    return d.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  const getFeedbackType = (feedback: AppFeedback) => {
    if (feedback.content.toLowerCase().includes('complaint')) return 'Complaint'
    if (feedback.content.toLowerCase().includes('suggestion')) return 'Suggestion'
    if (feedback.content.toLowerCase().includes('maintenance')) return 'Maintenance'
    if (feedback.content.toLowerCase().includes('issue')) return 'Issue'
    if (feedback.content.toLowerCase().includes('praise')) return 'Praise'
    return 'General Feedback'
  }

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`text-xl ${star <= rating ? 'text-orange-400' : 'text-gray-300'}`}
          >
            ★
          </span>
        ))}
      </div>
    )
  }

  const renderField = (label: string, value: React.ReactNode) => (
  <div className="grid grid-cols-[100px_1fr] sm:grid-cols-[140px_1fr] gap-2 items-start">
    <span className="text-xs sm:text-sm font-medium text-gray-700">
      {label}
    </span>
    <span className="text-xs sm:text-sm text-[#495057] font-medium break-words">
      {value ?? 'N/A'}
    </span>
  </div>
)

  return (
    <Modal
      open={open}
      close={onClose}
      title="Feedback Details"
      widthClass="w-[calc(100%-32px)] sm:max-w-lg md:max-w-[540px]"
      footer={
        <div className="flex justify-end gap-3">
          <Button text="Close" action={onClose} />
        </div>
      }
      showClose={false}
    >
      {feedback ? (
        <div className="w-full space-y-4 sm:space-y-5">
          {/* User Info Section */}
          <div className="flex items-start space-x-3 pb-3 border-b border-gray-200">
            <img
              src={feedback.submittedById.avatar || profileIcon}
              alt="User avatar"
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover"
            />
            <div className="flex-1">
              <div className="space-y-2">
                {renderField('Name:', feedback.submittedById.fullName)}
                {renderField('Email:', feedback.submittedById.email)}
                {renderField('Position:', 
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    feedback.submittedByType?.toLowerCase() === 'resident' 
                      ? 'bg-gray-100 text-gray-800' 
                      : feedback.submittedByType?.toLowerCase() === 'staff'
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {feedback.submittedByType === 'resident' ? 'Resident' : 
                     feedback.submittedByType === 'staff' ? 'Staff' : 
                     feedback.submittedByType || 'N/A'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Feedback Details */}
          <div className="space-y-3 sm:space-y-4">
            <div className="space-y-2 sm:space-y-3">
              {renderField('Feedback Type:', 
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {getFeedbackType(feedback)}
                </span>
              )}
              {renderField('Rating:', renderStars(feedback.rating))}
              {renderField('Submitted On:', formatDateTime(feedback.createdAt))}
            </div>
          </div>

          {/* Feedback Content */}
          <div className="space-y-2 sm:space-y-3">
            {renderField('Feedback:', 
                <p className="text-gray-800 whitespace-pre-wrap leading-relaxed text-xs sm:text-sm">
                  {feedback.content}
                </p>
            )}
          </div>

          {/* Additional Info - Category (if available) */}
          {(feedback as any).category && (
            <div className="space-y-2 sm:space-y-3">
              {renderField('Category:', (feedback as any).category)}
            </div>
          )}

          {/* Additional Info - Status (if available) */}
          {(feedback as any).status && (
            <div className="space-y-2 sm:space-y-3">
              {renderField('Status:', 
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  (feedback as any).status === 'resolved' 
                    ? 'bg-green-100 text-green-800'
                    : (feedback as any).status === 'in-progress'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {(feedback as any).status}
                </span>
              )}
            </div>
          )}

          {/* Attachments (if available) */}
          {(feedback as any).attachments && (feedback as any).attachments.length > 0 && (
            <div className="space-y-2 sm:space-y-3">
              {renderField('Attachments:', 
                <div className="space-y-2">
                  {(feedback as any).attachments.map((attachment: any, index: number) => (
                    <a
                      key={index}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors duration-200 text-xs sm:text-sm"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      {attachment.name || `Attachment ${index + 1}`}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs sm:text-sm text-gray-500">No feedback details available.</p>
      )}
    </Modal>
  )
}

export default FeedbackDetailsModal